import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk } from "../../app/store";
import { searchAggregations } from "../../app/api";
import { NestedAggregationFacet } from "../../../from-server/asin-central/search";
import {
  AggregationRowBucket,
  AggregationStepCount,
  PivotColumn,
  pivotCellKey,
  RowGroupLevel,
  StepBreakdownAggregationResponse,
} from "./stepBreakdown";

/**
 * Owns the pivot's `/search-agg` request lifecycle. Mirrors the
 * Slice 1 `statusOverviewAggregationConnector` pattern: a Redux
 * Toolkit slice with `start` / `success` / `failure` reducers, plus
 * an `AppThunk` that dispatches `start`, calls the middleware, and
 * dispatches `success` or `failure` keyed on the request's own
 * `queryString`.
 *
 * The connector holds the wire-format boundary: it translates the
 * nested DACS response envelope (`WireStepBreakdownResponse`) into
 * the derived, invariant-checked `StepBreakdownAggregationResponse`
 * defined in `stepBreakdown.ts` so the pure derivation module never
 * sees wire concerns (nullable fields, string enums, snake_case).
 */

export type StepBreakdownAggregationStatus =
  | "idle"
  | "loading"
  | "success"
  | "error";

export interface StepBreakdownAggregationState {
  readonly status: StepBreakdownAggregationStatus;
  readonly response: StepBreakdownAggregationResponse | undefined;
  readonly lastFetchedQueryString: string | undefined;
  readonly error: string | undefined;
}

const initialState: StepBreakdownAggregationState = {
  status: "idle",
  response: undefined,
  lastFetchedQueryString: undefined,
  error: undefined,
};

const REQUEST_TIMEOUT_MS = 30_000;
const TIMEOUT_ERROR_MESSAGE = "Request timed out after 30 seconds.";

// Sub-aggregation names in the response. Pulled out as constants so the
// request builder (`PIVOT_AGG_PAYLOAD`) and the response parser
// (`translateWireToResponse`) reference the same strings and can't drift.
// Terms sub-aggregations are keyed by their `termsField` value; filter
// sub-aggregations are keyed by their `filter.name` value.
const AGG_PRODUCT_LINE = "productLineId";
const AGG_PROGRAM = "programId";
const AGG_COUNTRY = "country";
const AGG_ASIN_TYPE = "asinType";
const AGG_LVS_STEPS = "validationDetailResultLVS.detail.step";
const AGG_HATTRICK_STEPS = "validationDetailResultHattrick.detail.step";
const FILTER_PASS_COUNT = "passCount";
const FILTER_APPLICABLE_COUNT = "applicableCount";

// Validation results treated as passing for pass-rate calculations.
// `pass` is a strict pass; `pass_exception` is a pass approved with a
// documented exception. Product treats both as passing on the pivot.
// DACS interprets a comma-delimited filter value as a multi-value terms
// query, so joining these tokens with a comma is the on-wire format.
const PASSING_RESULT_VALUES: readonly string[] = ["pass", "pass_exception"];

// Validation results that count toward a step's denominator. A step
// result of `not_applicable` means the step did not run for that
// ASIN/marketplace, so it must NOT inflate the denominator — otherwise
// a step that is N/A for every row renders as `0% (0/N)` and reads like
// a failure. Counting only pass + pass_exception + fail makes the
// per-cell fraction reflect only steps that actually ran, and lets the
// derivation drop a column whose applicable total is zero across all
// rows.
const APPLICABLE_RESULT_VALUES: readonly string[] = [
  "pass",
  "pass_exception",
  "fail",
];

// Nested-aggregation POST body mirrors the OpenSearch tree from the
// design's `/search-agg` extension snippet: productLineId → programId →
// country → asinType → { lvs steps, hattrick steps }. Each leaf carries
// two sibling filter sub-aggregations: `passCount` counts docs whose
// result is pass/pass_exception, and `applicableCount` counts docs whose
// result is pass/pass_exception/fail. The applicable count — not the
// step bucket's raw documentCount — is the cell denominator, so
// `not_applicable` results are excluded and an all-N/A column can be
// dropped. The tree is invariant across `queryString` changes, so it is
// built once as a module-level constant.
const STEP_FILTERS = (
  resultField: string
): NonNullable<NestedAggregationFacet["filters"]> => [
  {
    field: resultField,
    // DACS interprets a comma-delimited filter value as a multi-value
    // terms query, so `pass_exception` (an approved-with-exception
    // outcome) counts toward the per-step pass rate alongside strict
    // `pass`.
    value: PASSING_RESULT_VALUES.join(","),
    name: FILTER_PASS_COUNT,
  },
  {
    field: resultField,
    value: APPLICABLE_RESULT_VALUES.join(","),
    name: FILTER_APPLICABLE_COUNT,
  },
];

const PIVOT_AGG_PAYLOAD: NestedAggregationFacet = {
  termsField: AGG_PRODUCT_LINE,
  size: 1000,
  subAggregations: [
    {
      termsField: AGG_PROGRAM,
      size: 1000,
      subAggregations: [
        {
          termsField: AGG_COUNTRY,
          size: 1000,
          subAggregations: [
            {
              termsField: AGG_ASIN_TYPE,
              size: 1000,
              subAggregations: [
                {
                  termsField: AGG_LVS_STEPS,
                  size: 1000,
                  nestedPath: "validationDetailResultLVS.detail",
                  filters: STEP_FILTERS(
                    "validationDetailResultLVS.detail.result"
                  ),
                },
                {
                  termsField: AGG_HATTRICK_STEPS,
                  size: 1000,
                  nestedPath: "validationDetailResultHattrick.detail",
                  filters: STEP_FILTERS(
                    "validationDetailResultHattrick.detail.result"
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

// Wire-format types: mirror the DACS `NestedAggregationResults` envelope.
// A recursive bucket carries a `key`, a `documentCount`, and an optional
// `subAggregations` map keyed by termsField / filter.name from the
// request. Each sub-aggregation result is either a `buckets` list (terms)
// or a `documentCount` scalar (filter), never both. Kept local to the
// connector so `stepBreakdown.ts` and downstream React never see the raw
// shape.
interface WireBucketList {
  readonly buckets?: readonly WireBucket[];
  readonly documentCount?: number;
}

interface WireBucket {
  readonly key?: string;
  readonly documentCount?: number;
  readonly subAggregations?: { readonly [name: string]: WireBucketList };
}

interface WireStepBreakdownResponse {
  readonly totalResultCount?: number;
  readonly nestedAggregationResults?: {
    readonly [name: string]: WireBucketList;
  };
}

/**
 * ID-to-name maps used to resolve opaque product-line and program IDs
 * returned by the aggregation (which groups by ID for filter-chip
 * fidelity) into human-readable row labels. The maps come from the
 * Slice 1 `productLines` and `upstreamAttributesData` slices, which
 * the FilterBar's chip config already relies on for its own label
 * lookups.
 */
interface RowGroupLookups {
  readonly productLineIdToName: ReadonlyMap<string, string>;
  readonly programIdToName: ReadonlyMap<string, string>;
}

const EMPTY_LOOKUPS: RowGroupLookups = {
  productLineIdToName: new Map<string, string>(),
  programIdToName: new Map<string, string>(),
};

/**
 * Snapshot the current product-line and program ID → name maps from
 * Redux state so `translateWireToResponse` can resolve opaque
 * aggregation-bucket keys into display labels. Reads defensively —
 * the maps degrade to empty if the corresponding Slice 1 fetches
 * haven't finished yet, and the fallback path in `buildRowBucket`
 * renders the raw ID.
 */
function buildRowGroupLookups(state: RootStateLike): RowGroupLookups {
  const productLines = state.productLines?.productLinesData?.values ?? [];
  const programs = state.upstreamAttributesData?.programNameData?.values ?? [];
  const productLineIdToName = new Map<string, string>();
  for (const entry of productLines) {
    if (
      entry &&
      typeof entry.id === "string" &&
      typeof entry.name === "string" &&
      entry.id !== "" &&
      entry.name !== ""
    ) {
      productLineIdToName.set(entry.id, entry.name);
    }
  }
  const programIdToName = new Map<string, string>();
  for (const entry of programs) {
    if (
      entry &&
      typeof entry.id === "string" &&
      typeof entry.name === "string" &&
      entry.id !== "" &&
      entry.name !== ""
    ) {
      programIdToName.set(entry.id, entry.name);
    }
  }
  if (productLineIdToName.size === 0 && programIdToName.size === 0) {
    return EMPTY_LOOKUPS;
  }
  return { productLineIdToName, programIdToName };
}

/**
 * Shape of the two Redux slices this connector reads at request time.
 * Kept as a structural interface so the connector stays decoupled from
 * the `RootState` type — the actual selectors live in Slice 1.
 */
interface RootStateLike {
  readonly productLines?: {
    readonly productLinesData?: {
      readonly values?: readonly {
        readonly id?: string;
        readonly name?: string;
      }[];
    };
  };
  readonly upstreamAttributesData?: {
    readonly programNameData?: {
      readonly values?: readonly {
        readonly id?: string;
        readonly name?: string;
      }[];
    };
  };
}

// Look up a sub-aggregation's bucket list by name. The name is a
// termsField for terms sub-aggs or a filter.name for filter sub-aggs
// (matches the request-side names in `PIVOT_AGG_PAYLOAD`). Missing
// entries return an empty list.
function bucketsFor(
  bucket: WireBucket,
  aggName: string
): readonly WireBucket[] {
  return bucket.subAggregations?.[aggName]?.buckets ?? [];
}

// Look up a filter sub-aggregation's documentCount by name. Missing
// filter results (or a filter result with no `documentCount`) return 0.
function filterCountFor(bucket: WireBucket, filterName: string): number {
  return bucket.subAggregations?.[filterName]?.documentCount ?? 0;
}

// Row-group child-level → termsField mapping used for downward
// recursion. Isolated to one place so the request tree and the parser
// stay symmetrical.
const CHILD_LEVEL_TO_AGG_NAME: Partial<Record<RowGroupLevel, string>> = {
  program: AGG_PROGRAM,
  marketplace: AGG_COUNTRY,
  asinType: AGG_ASIN_TYPE,
};

function childBucketsFor(
  bucket: WireBucket,
  childLevel: RowGroupLevel
): readonly WireBucket[] {
  const aggName = CHILD_LEVEL_TO_AGG_NAME[childLevel];
  return aggName === undefined ? [] : bucketsFor(bucket, aggName);
}

// Returns a plain object rather than a Map so the whole
// `StepBreakdownAggregationResponse` stays serializable inside Redux
// state (Redux Toolkit's `serializableCheck` middleware rejects Map
// values, and DevTools cannot round-trip them).
//
// `totalCount` is the `applicableCount` filter (pass + pass_exception +
// fail), NOT the step bucket's raw `documentCount`. The raw count
// includes `not_applicable` sub-docs, which would inflate the
// denominator and make an all-N/A step read as `0% (0/N)`. Using the
// applicable count keeps the fraction meaningful and lets the
// derivation drop columns whose applicable total is zero everywhere.
function buildStepsRecord(
  bucket: WireBucket
): Readonly<Record<string, AggregationStepCount>> | undefined {
  const lvs = bucketsFor(bucket, AGG_LVS_STEPS);
  const ht = bucketsFor(bucket, AGG_HATTRICK_STEPS);
  if (lvs.length === 0 && ht.length === 0) {
    return undefined;
  }
  const record: Record<string, AggregationStepCount> = {};
  for (const s of lvs) {
    if (s.key === undefined) continue;
    record[pivotCellKey({ pipeline: "LVS", stepName: s.key })] = {
      passCount: filterCountFor(s, FILTER_PASS_COUNT),
      totalCount: filterCountFor(s, FILTER_APPLICABLE_COUNT),
    };
  }
  for (const s of ht) {
    if (s.key === undefined) continue;
    record[pivotCellKey({ pipeline: "HATTRICK", stepName: s.key })] = {
      passCount: filterCountFor(s, FILTER_PASS_COUNT),
      totalCount: filterCountFor(s, FILTER_APPLICABLE_COUNT),
    };
  }
  return record;
}

function buildRowBucket(
  wire: WireBucket,
  level: RowGroupLevel,
  lookups: RowGroupLookups
): AggregationRowBucket {
  const childLevel = nextLevel(level);
  const rawChildren =
    childLevel !== undefined ? childBucketsFor(wire, childLevel) : [];
  const children =
    rawChildren.length > 0 && childLevel !== undefined
      ? rawChildren.map((c) => buildRowBucket(c, childLevel, lookups))
      : undefined;

  const rawKey = wire.key ?? "";
  let label = rawKey;
  let filterValue: string | undefined;
  if (level === "productLine") {
    const name = lookups.productLineIdToName.get(rawKey);
    if (name !== undefined && name !== rawKey) {
      label = name;
      filterValue = rawKey;
    }
  } else if (level === "program") {
    const name = lookups.programIdToName.get(rawKey);
    if (name !== undefined && name !== rawKey) {
      label = name;
      filterValue = rawKey;
    }
  }

  return {
    level,
    label,
    filterValue,
    count: wire.documentCount ?? 0,
    steps: buildStepsRecord(wire),
    children,
  };
}

function nextLevel(level: RowGroupLevel): RowGroupLevel | undefined {
  switch (level) {
    case "productLine":
      return "program";
    case "program":
      return "marketplace";
    case "marketplace":
      return "asinType";
    default:
      return undefined;
  }
}

function collectPipelineStepNames(
  buckets: readonly WireBucket[],
  lvs: string[],
  ht: string[],
  seenLvs: Set<string>,
  seenHt: Set<string>
): void {
  for (const b of buckets) {
    for (const s of bucketsFor(b, AGG_LVS_STEPS)) {
      if (s.key !== undefined && !seenLvs.has(s.key)) {
        seenLvs.add(s.key);
        lvs.push(s.key);
      }
    }
    for (const s of bucketsFor(b, AGG_HATTRICK_STEPS)) {
      if (s.key !== undefined && !seenHt.has(s.key)) {
        seenHt.add(s.key);
        ht.push(s.key);
      }
    }
    // Recurse only through row-group child levels — step buckets are
    // leaves and hold filter results, not further row groups.
    for (const aggName of [AGG_PROGRAM, AGG_COUNTRY, AGG_ASIN_TYPE]) {
      const nested = bucketsFor(b, aggName);
      if (nested.length > 0) {
        collectPipelineStepNames(nested, lvs, ht, seenLvs, seenHt);
      }
    }
  }
}

function translateWireToResponse(
  wire: WireStepBreakdownResponse,
  lookups: RowGroupLookups
): StepBreakdownAggregationResponse {
  const topBuckets =
    wire.nestedAggregationResults?.[AGG_PRODUCT_LINE]?.buckets ?? [];

  const lvs: string[] = [];
  const ht: string[] = [];
  collectPipelineStepNames(topBuckets, lvs, ht, new Set(), new Set());
  const steps: readonly PivotColumn[] = [
    ...lvs.map<PivotColumn>((s) => ({
      stepName: s,
      pipeline: "LVS",
      headerLabel: s,
    })),
    ...ht.map<PivotColumn>((s) => ({
      stepName: s,
      pipeline: "HATTRICK",
      headerLabel: s,
    })),
  ];

  const rows = topBuckets.map((b) => buildRowBucket(b, "productLine", lookups));

  return {
    totalCount: wire.totalResultCount ?? 0,
    rows,
    steps,
  };
}

export const stepBreakdownAggregationSlice = createSlice({
  name: "stepBreakdownAggregation",
  initialState,
  reducers: {
    stepBreakdownAggregationStart(
      state: StepBreakdownAggregationState,
      { payload }: PayloadAction<{ queryString: string }>
    ) {
      // Fresh filter: drop the prior pivot so the component falls into
      // its full "Loading Step Breakdown…" branch instead of the
      // "Refreshing…" bar sitting above a pivot that reflects an
      // older query.
      if (payload.queryString !== state.lastFetchedQueryString) {
        state.response = undefined;
      }
      state.status = "loading";
      state.error = undefined;
      state.lastFetchedQueryString = payload.queryString;
    },
    stepBreakdownAggregationSuccess(
      state: StepBreakdownAggregationState,
      { payload }: PayloadAction<StepBreakdownAggregationResponse>
    ) {
      state.status = "success";
      state.response = payload;
      state.error = undefined;
    },
    stepBreakdownAggregationFailure(
      state: StepBreakdownAggregationState,
      { payload }: PayloadAction<string>
    ) {
      state.status = "error";
      state.error = payload;
    },
  },
});

export const {
  stepBreakdownAggregationStart,
  stepBreakdownAggregationSuccess,
  stepBreakdownAggregationFailure,
} = stepBreakdownAggregationSlice.actions;

export default stepBreakdownAggregationSlice.reducer;

// Guards against out-of-order responses: only commit `success` or
// `failure` when this thunk still owns the most recent
// `lastFetchedQueryString` and the `generateQuery` slice's
// `queryString` hasn't advanced past our request. A slower response
// to an older query is dropped so it can't overwrite fresher data.
// Timeout is enforced client-side via an `AbortController` + a race
// against a 30-second timer; on timeout the connector dispatches a
// friendly error string the pivot's error UI renders.
export const fetchStepBreakdownAggregation =
  (queryString: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(stepBreakdownAggregationStart({ queryString }));

    const controller = new AbortController();
    let didTimeOut = false;
    const timeoutId = setTimeout(() => {
      didTimeOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    const timeoutPromise = new Promise<never>((_, reject) => {
      const onAbort = () => {
        reject(new Error(TIMEOUT_ERROR_MESSAGE));
      };
      if (controller.signal.aborted) {
        onAbort();
      } else {
        controller.signal.addEventListener("abort", onAbort, { once: true });
      }
    });

    try {
      const raw = await Promise.race([
        searchAggregations(queryString, [], PIVOT_AGG_PAYLOAD),
        timeoutPromise,
      ]);
      const state = getState();
      const stillOwnedByConnector =
        state.stepBreakdownAggregation.lastFetchedQueryString === queryString;
      const stillCurrentInGenerateQuery =
        state.generateQuery.queryString === queryString;
      if (!stillOwnedByConnector || !stillCurrentInGenerateQuery) {
        return;
      }
      const wire = raw as unknown as WireStepBreakdownResponse;
      const lookups = buildRowGroupLookups(state);
      dispatch(
        stepBreakdownAggregationSuccess(translateWireToResponse(wire, lookups))
      );
    } catch (err: unknown) {
      const state = getState();
      if (
        state.stepBreakdownAggregation.lastFetchedQueryString !== queryString
      ) {
        return;
      }
      if (didTimeOut) {
        dispatch(stepBreakdownAggregationFailure(TIMEOUT_ERROR_MESSAGE));
      } else {
        const message = err instanceof Error ? err.message : String(err);
        dispatch(stepBreakdownAggregationFailure(message));
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };
